import React, { useState, useEffect } from 'react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { transactionAPI } from '@/services/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Receipt, Search, Eye, CheckCircle, XCircle, Clock, MessageSquare, Send, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const MyPayments = () => {
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTxn, setSelectedTxn] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isRespondOpen, setIsRespondOpen] = useState(false);
  const [infoResponse, setInfoResponse] = useState('');
  const [responding, setResponding] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const res = await transactionAPI.list({ limit: 100 });
      setTransactions(res.data);
    } catch (err) {
      toast.error('Failed to load payments');
    } finally {
      setLoading(false);
    }
  };

  const handleRespondInfo = async () => {
    if (!infoResponse.trim()) {
      toast.error('Please provide a response');
      return;
    }
    setResponding(true);
    try {
      await transactionAPI.respondInfo(selectedTxn.transaction_id, infoResponse);
      toast.success('Information submitted');
      setIsRespondOpen(false);
      setIsDetailOpen(false);
      setInfoResponse('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit');
    } finally {
      setResponding(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(amount || 0);
  };

  const getStatusStyle = (status) => {
    const styles = {
      'COMPLETED': 'status-success',
      'PENDING_APPROVAL': 'status-pending',
      'REJECTED': 'status-error',
      'PROCESSING': 'status-info',
      'INFO_REQUESTED': 'status-pending',
      'CANCELLED': 'status-error'
    };
    return styles[status] || 'status-info';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'COMPLETED': return <CheckCircle className="w-5 h-5 text-blue-600" />;
      case 'REJECTED': return <XCircle className="w-5 h-5 text-red-500" />;
      case 'INFO_REQUESTED': return <MessageSquare className="w-5 h-5 text-blue-500" />;
      default: return <Clock className="w-5 h-5 text-amber-500" />;
    }
  };

  const filteredTransactions = transactions.filter(txn => {
    if (statusFilter !== 'all' && txn.status !== statusFilter) return false;
    if (searchTerm && !txn.beneficiary_name.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-blue-600/30 border-t-blue-600 rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="my-payments-page">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 font-['Outfit']">My Payments</h1>
          <p className="text-gray-500 mt-1">Track your payment history and status</p>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
            <Input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search beneficiary..." className="pl-10 bg-white/50 border-gray-200 text-gray-900" />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-48 bg-white/50 border-gray-200 text-gray-900">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-gray-50 border-gray-300">
              <SelectItem value="all" className="text-gray-900">All Status</SelectItem>
              <SelectItem value="COMPLETED" className="text-gray-900">Completed</SelectItem>
              <SelectItem value="PENDING_APPROVAL" className="text-gray-900">Pending</SelectItem>
              <SelectItem value="REJECTED" className="text-gray-900">Rejected</SelectItem>
              <SelectItem value="INFO_REQUESTED" className="text-gray-900">Info Requested</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Payments List */}
        {filteredTransactions.length > 0 ? (
          <div className="space-y-3">
            {filteredTransactions.map((txn) => (
              <Card key={txn.transaction_id} className="glass-card hover:border-blue-600/20 transition-colors">
                <CardContent className="p-4">
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="flex items-center gap-4 flex-1">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${txn.status === 'COMPLETED' ? 'bg-blue-900/10' : txn.status === 'REJECTED' ? 'bg-red-500/10' : txn.status === 'INFO_REQUESTED' ? 'bg-blue-500/10' : 'bg-amber-500/10'}`}>
                        {getStatusIcon(txn.status)}
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900">{txn.beneficiary_name}</p>
                        <p className="text-sm text-gray-500">{txn.description}</p>
                        <p className="text-xs text-gray-400 mt-1">{new Date(txn.submitted_at).toLocaleString()}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-xl font-bold text-gray-900 font-['JetBrains_Mono']">{formatCurrency(txn.amount)}</p>
                        <Badge className={getStatusStyle(txn.status)}>{txn.status.replace(/_/g, ' ')}</Badge>
                      </div>
                      <Button variant="outline" size="sm" onClick={() => { setSelectedTxn(txn); setIsDetailOpen(true); }} className="border-gray-300 text-gray-600">
                        <Eye className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  
                  {txn.status === 'INFO_REQUESTED' && (
                    <div className="mt-4 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                      <p className="text-xs text-blue-400 mb-1">Information Requested:</p>
                      <p className="text-sm text-gray-900">{txn.info_requested}</p>
                      <Button onClick={() => { setSelectedTxn(txn); setIsRespondOpen(true); }} size="sm" className="mt-2 bg-blue-500 hover:bg-blue-600">
                        <Send className="w-4 h-4 mr-2" /> Respond
                      </Button>
                    </div>
                  )}
                  
                  {txn.status === 'REJECTED' && txn.rejection_reason && (
                    <div className="mt-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                      <p className="text-xs text-red-400 mb-1">Rejection Reason:</p>
                      <p className="text-sm text-gray-900">{txn.rejection_reason}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="glass-card">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <Receipt className="w-16 h-16 text-gray-300 mb-4" />
              <p className="text-gray-500">{searchTerm || statusFilter !== 'all' ? 'No payments match your filters' : 'No payments yet'}</p>
            </CardContent>
          </Card>
        )}

        {/* Detail Dialog */}
        <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
          <DialogContent className="bg-white border-gray-200 max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-gray-900">Payment Details</DialogTitle>
            </DialogHeader>
            {selectedTxn && (
              <div className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div><p className="text-xs text-gray-500">Beneficiary</p><p className="text-gray-900">{selectedTxn.beneficiary_name}</p></div>
                  <div><p className="text-xs text-gray-500">Amount</p><p className="text-gray-900 font-['JetBrains_Mono']">{formatCurrency(selectedTxn.amount)}</p></div>
                  <div><p className="text-xs text-gray-500">Status</p><Badge className={getStatusStyle(selectedTxn.status)}>{selectedTxn.status}</Badge></div>
                  <div><p className="text-xs text-gray-500">Date</p><p className="text-gray-900 text-sm">{new Date(selectedTxn.submitted_at).toLocaleString()}</p></div>
                </div>
                <div><p className="text-xs text-gray-500">Description</p><p className="text-gray-900">{selectedTxn.description}</p></div>
                <div><p className="text-xs text-gray-500">Beneficiary Details</p><p className="text-gray-900 font-['JetBrains_Mono'] text-sm">{selectedTxn.beneficiary_details}</p></div>
                {selectedTxn.banking_partner_ref && (
                  <div><p className="text-xs text-gray-500">Reference</p><p className="text-gray-900 font-['JetBrains_Mono'] text-sm">{selectedTxn.banking_partner_ref}</p></div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Respond Dialog */}
        <Dialog open={isRespondOpen} onOpenChange={setIsRespondOpen}>
          <DialogContent className="bg-white border-gray-200">
            <DialogHeader>
              <DialogTitle className="text-gray-900">Provide Information</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              {selectedTxn && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">Requested Information:</p>
                  <p className="text-sm text-gray-900">{selectedTxn.info_requested}</p>
                </div>
              )}
              <Textarea value={infoResponse} onChange={(e) => setInfoResponse(e.target.value)} placeholder="Your response..." className="bg-gray-50 border-gray-300 text-gray-900 min-h-24" />
              <Button onClick={handleRespondInfo} className="w-full bg-blue-900 hover:bg-blue-800" disabled={responding}>
                {responding ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />} Submit Response
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default MyPayments;
